import { Plus } from "lucide-react";
import { PlanItem } from "@/components/planner/PlanItem";
import { iso, parse, addDays, byDate } from "@/lib/planner";

const DayHead = ({ d, items, today }) => {
  const done = items.filter((i) => i.done).length;
  return (
    <div className="flex items-center justify-between mb-2">
      <span className={`text-xs font-semibold uppercase tracking-widest ${iso(d) === today ? "text-[#C87D53]" : "text-stone-500"}`}>{d.toLocaleDateString([], { weekday: "short", day: "numeric" })}{iso(d) === today && " · Today"}</span>
      {items.length > 0 && <span className="text-[11px] text-stone-500">{done}/{items.length}</span>}
    </div>
  );
};

export const DayList = ({ items, ...p }) => (
  <div className="space-y-3" data-testid="plan-day-view">
    {items.map((i) => <PlanItem key={i.id} item={i} {...p} />)}
    {!items.length && <p className="text-stone-500 text-sm" data-testid="plan-empty">Nothing planned for this day. Enjoy some free exploring!</p>}
  </div>
);

export const WeekGrid = ({ start, items, today, editable, onAdd, ...p }) => {
  const m = byDate(items);
  return (
    <div className="grid md:grid-cols-5 gap-4" data-testid="plan-week-view">
      {[0, 1, 2, 3, 4].map((n) => { const d = addDays(start, n); const its = m[iso(d)] || []; return (
        <div key={n} className={`rounded-3xl p-3 border ${iso(d) === today ? "bg-[#FBF3E6] border-[#C87D53]/40" : "bg-[#F7F3EB] border-[#E6DEC3]"}`} data-testid={`plan-week-day-${iso(d)}`}>
          <DayHead d={d} items={its} today={today} />
          <div className="space-y-2">{its.map((i) => <PlanItem key={i.id} item={i} editable={editable} compact {...p} />)}</div>
          {editable && <button onClick={() => onAdd(iso(d))} data-testid={`plan-add-${iso(d)}`} className="mt-2 w-full text-xs py-1.5 rounded-full border border-dashed border-[#C87D53]/50 text-[#C87D53] inline-flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Add</button>}
        </div>
      ); })}
    </div>
  );
};

export const MonthGrid = ({ start, end, month, items, today, onPick }) => {
  const m = byDate(items);
  const weeks = [];
  for (let d = start; d <= end; d = addDays(d, 7)) weeks.push(d);
  return (
    <div data-testid="plan-month-view">
      <div className="grid grid-cols-5 gap-2 mb-2">{["Mon", "Tue", "Wed", "Thu", "Fri"].map((d) => <span key={d} className="text-xs uppercase tracking-widest text-stone-500 px-2">{d}</span>)}</div>
      {weeks.map((w) => (
        <div key={iso(w)} className="grid grid-cols-5 gap-2 mb-2">
          {[0, 1, 2, 3, 4].map((n) => { const d = addDays(w, n); const its = m[iso(d)] || []; const done = its.filter((i) => i.done).length; const out = d.getMonth() !== month; return (
            <button key={n} onClick={() => onPick(parse(iso(d)))} data-testid={`plan-month-day-${iso(d)}`} className={`text-left rounded-2xl p-2.5 min-h-[92px] border transition-colors hover:border-[#2C4A3E] ${iso(d) === today ? "border-[#C87D53] bg-[#FBF3E6]" : "border-[#E6DEC3] bg-white/70"} ${out ? "opacity-40" : ""}`}>
              <span className="text-sm font-semibold text-[#2C4A3E]">{d.getDate()}</span>
              {its.length > 0 && <div className="mt-1.5 h-1.5 rounded-full bg-[#F1EADB] overflow-hidden"><div className="h-full bg-[#6B9080]" style={{ width: `${(100 * done) / its.length}%` }} /></div>}
              <ul className="mt-1.5 space-y-0.5">{its.slice(0, 3).map((i) => <li key={i.id} className={`text-[10px] truncate ${i.done ? "line-through text-stone-400" : "text-stone-600"}`}>{i.subject_short || i.subject_name}: {i.topic}</li>)}</ul>
              {its.length > 3 && <span className="text-[10px] text-stone-400">+{its.length - 3} more</span>}
            </button>
          ); })}
        </div>
      ))}
    </div>
  );
};
